/**
 * useLoanChatFabHidden — the "hide the floating chat bubble" preference.
 *
 * Persisted (partner-scoped) so it sticks across reloads, with a module-level
 * pub/sub so toggling it in one place (e.g. the Chat tab) instantly updates the
 * FAB in another subtree — a plain `storage` event only fires across tabs, not
 * within the same document, so we notify subscribers directly.
 *
 * Returns [hidden, setHidden].
 */
import { useCallback, useEffect, useState } from "react";

import { partnerStorage } from "../../../../utils/storage/partnerStorage";

const KEY = "loanChat.fabHidden";
const listeners = new Set();
let cached = null;

const read = () => {
  if (cached === null) {
    try {
      cached = partnerStorage.getItem(KEY) === "1";
    } catch {
      cached = false;
    }
  }
  return cached;
};

const write = (hidden) => {
  cached = !!hidden;
  try {
    partnerStorage.setItem(KEY, cached ? "1" : "0");
  } catch {
    // ignore storage failures — the in-memory value still drives the UI
  }
  listeners.forEach((fn) => fn(cached));
};

export function useLoanChatFabHidden() {
  const [hidden, setHiddenState] = useState(read);

  useEffect(() => {
    const fn = (h) => setHiddenState(h);
    listeners.add(fn);
    // Re-sync in case the value changed between render and subscribe.
    setHiddenState(read());
    return () => listeners.delete(fn);
  }, []);

  const setHidden = useCallback((h) => write(h), []);
  return [hidden, setHidden];
}

export default useLoanChatFabHidden;
